import type { Capabilities, MeResponse, Membership, WorkspaceRef } from "@ondo/shared";

import { About } from "../about/About.tsx";
import { Activity } from "../activity/Activity.tsx";
import { Collection } from "../collection/Collection.tsx";
import { Dashboard } from "../dashboard/Dashboard.tsx";
import { Entities } from "../entities/Entities.tsx";
import { Notifications } from "../notifications/Notifications.tsx";
import { NotificationPreferences } from "../notificationPreferences/NotificationPreferences.tsx";
import { Profile } from "../profile/Profile.tsx";
import { RegisterArtwork } from "../register/RegisterArtwork.tsx";
import { ShareCodes } from "../shareCodes/ShareCodes.tsx";
import { readOwnedIssuableWorks } from "../shareCodes/useShareCodesController.ts";
import { OWNER_SHARE_CODES_COPY } from "../shareCodes/shareCodes.constants.ts";
import { NotBuiltYet } from "../shell/NotBuiltYet.tsx";
import { PortalShell } from "../shell/PortalShell.tsx";
import { inboxPrincipalFor } from "../shell/shell.model.ts";
import { Arrangements } from "../arrangements/Arrangements.tsx";
import { BorrowedLoans } from "../loans/BorrowedLoans.tsx";
import { TransferOffers } from "../transfers/TransferOffers.tsx";
import { ArtistSettlements } from "./ArtistSettlements.tsx";
import { Verifications } from "../verifications/Verifications.tsx";
import { ViewingRooms } from "../viewingRooms/ViewingRooms.tsx";
import { WorkRecord } from "../work/WorkRecord.tsx";
import { COLLECTOR_COPY, COLLECTOR_PORTAL } from "./collector.constants.ts";
import type { CollectorDestination } from "./collector.model.ts";
import type { PortalView } from "../shell/shell.model.ts";
import { workspacePath } from "../routes/routes.model.ts";

/**
 * The collector's workspace.
 *
 * The frame is `PortalShell` and is shared with every other portal; what is left
 * here is the one thing that genuinely differs between two of them — which
 * component each destination renders.
 */
export function CollectorWorkspace({
  destination,
  record,
  workspace,
  principal,
  capabilities,
  membership,
  canSwitch,
  onSignOut,
}: {
  destination: CollectorDestination;
  /** Which work a `work` destination is about. Null for every other one. */
  record: string | null;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  /** The caller's membership of this workspace's organisation, or null. */
  membership: Membership | null;
  canSwitch: boolean;
  onSignOut: () => void;
}) {
  return (
    <PortalShell
      spec={COLLECTOR_PORTAL}
      destination={destination}
      workspace={workspace}
      principal={principal}
      capabilities={capabilities}
      membership={membership}
      canSwitch={canSwitch}
      onSignOut={onSignOut}
    >
      {(view) => (
        <Destination
          destination={destination}
          record={record}
          workspace={workspace}
          capabilities={capabilities}
          principal={principal}
          view={view}
          onSignOut={onSignOut}
        />
      )}
    </PortalShell>
  );
}

/** The one place a switch over the collector's destinations lives. */
function Destination({
  destination,
  record,
  workspace,
  capabilities,
  principal,
  view,
  onSignOut,
}: {
  destination: CollectorDestination;
  record: string | null;
  workspace: WorkspaceRef;
  capabilities: Capabilities;
  principal: MeResponse["principal"];
  view: PortalView;
  onSignOut: () => void;
}) {
  const collectionHref = workspacePath(workspace, "collection");
  const registerHref = workspacePath(workspace, "register");
  const workBase = view.hrefs.workBase;

  switch (destination) {
    case "dashboard":
      return (
        <Dashboard
          registerHref={registerHref}
          collectionHref={collectionHref}
          workBase={workBase}
          viewerOndoId={principal.ondoId}
        />
      );
    case "collection":
      return (
        <Collection capabilities={capabilities} workBase={workBase} />
      );
    // NO PROPS, AND THE ABSENCE IS THE POINT — OND-52. The screen is about
    // every entity the caller acts for, so it takes neither this workspace nor
    // its organisation: it reads the account from `useAccount` and the holdings
    // from the collections list, both of which already span every entity.
    // Passing the open workspace in would invite a filter by it, which is the
    // single-entity view the ticket exists to replace.
    case "entities":
      return <Entities />;
    case "register":
      return <RegisterArtwork collectionHref={collectionHref} workBase={workBase} />;
    // Whose inbox this configures is the same question the inbox itself asks,
    // answered the same way. `canWrite` is true on a personal workspace because
    // `atLeast`'s null branch is — a person always administers themselves.
    case "notification-preferences":
      return <NotificationPreferences canWrite />;

    case "about":
      return <About />;
    // The reader's own ONDO id, so their acts read "You" rather than their name.
    // A `user:` feed carries what the account did AND what was done to what it
    // holds, so both sides appear and only the id tells them apart.
    case "activity":
      // NO `administers` HERE. A collector workspace is one person's, so an
      // empty trail can only mean "you have opened nothing" — which is the
      // prop's default and the only true sentence available.
      return <Activity workBase={workBase} viewerOndoId={principal.ondoId} />;
    // Takes the workspace rather than the principal, because WHOSE profile this
    // is follows from whether the workspace has an organisation — a personal one
    // opens onto the reader, an organisation's onto the entity. See Profile.tsx.
    case "profile":
      return <Profile workspace={workspace} onSignOut={onSignOut} />;
    // Scoped to this workspace's own principal — a personal collector workspace
    // is the person, so a request addressed to a gallery they work at belongs in
    // that gallery's inbox and not in this one. That was the comment here before
    // `inboxPrincipalFor` existed, above a line that passed the person either
    // way; the rule now has a function rather than a paragraph.
    case "verifications":
      return <Verifications principalOndoId={inboxPrincipalFor(principal, workspace)} />;

    // TITLE OFFERED TO YOU, and the screen that makes an on-ONDO sale
    // finishable. `ondo.effect_sale()` records the sale and OFFERS title; it
    // cannot move it, because the buyer has consented to nothing at the moment
    // the gallery presses Convert. Without this the row parks at
    // `status = 'offered'` and nothing anywhere can settle it.
    // The view ACROSS works the rail promised: who represents what, and what a
    // gallery holds. Reads `ondo.my_arrangements()`; ending any of it is on
    // the work's own record, which every row links to.
    case "loans":
      return <Arrangements workBase={view.hrefs.workBase} />;
    case "offers":
      return <TransferOffers workBase={view.hrefs.workBase} selfOndoId={principal.ondoId} />;

    // THE OTHER THING SOMEBODY CAN HAND YOU, and deliberately not the same
    // screen. An offer of TITLE and an offer of POSSESSION are different facts
    // with different answers — accepting title needs a collection to put the
    // work in, accepting a loan needs nothing — and `ondo.loans` exists because
    // the registry refuses to conflate the two.
    case "borrowed":
      return <BorrowedLoans workBase={view.hrefs.workBase} />;

    // WHAT A COVENANT OWES YOU. The other side of the gallery's settlements
    // screen, and it exists for one control: ONDO moves no money, so a
    // remittance is a gallery asserting something about its own conduct toward
    // you, and everywhere else in this system a claim about somebody can be
    // answered by them.
    case "settlements":
      return <ArtistSettlements workBase={view.hrefs.workBase} />;

    // WHO HAS BEEN SHOWN A WORK OF YOURS. The same screen the agent rail mounts,
    // over the works this reader OWNS rather than the ones they represent —
    // `readOwnedIssuableWorks` reads `ondo.search_owned_works()`, which is scoped
    // by `owned_work_ids()`, while `my_agent_mandates()` is empty for an owner and
    // would have left the picker with nothing to offer.
    //
    // GATED ON `consignWork`, WHICH IS A COINCIDENCE AND IS NAMED AS ONE.
    // 20260910090000 widened that capability to `owned_work_ids() ∪
    // agented_curatable_work_ids()` to mirror `ondo.offer_consignment()`, and
    // that is exactly the union `ondo.issue_share_code()` tests — so today it
    // answers this question correctly by describing the same set. The agent's
    // rung check cannot be reused: `membership` is null on a personal collector
    // workspace, so `atLeast(membership?.level, "editor")` is false for every
    // owner. The day consigning and sharing stop describing one set — a work an
    // owner may show but not place would do it — this wants a `shareWork`
    // capability of its own. That migration earns itself then, not now: a second
    // capability computed from the identical expression is two places for one
    // rule to drift, which is what CLAUDE.md says about derived roles.
    case "share-codes":
      return (
        <ShareCodes
          workBase={workBase}
          // The firm on an organisation's collector workspace, the reader alone
          // on a personal one — the resolution the agent's mount uses, and
          // `issue_share_code` checks the caller is an editor of whatever it is.
          asOndoId={workspace.organisation?.ondoId ?? null}
          canIssue={capabilities.consignWork}
          cannotIssue={COLLECTOR_COPY.shareCodesNeedWork}
          readIssuableWorks={readOwnedIssuableWorks}
          copy={OWNER_SHARE_CODES_COPY}
        />
      );
    // Takes the whole workspace rather than a set of hrefs, because a row's
    // action can go to any of four screens with a focus on the URL, and
    // `notifications.model.ts` builds each through `routes.model.ts` — which is
    // the one place in this app that knows what a path looks like.
    case "notifications":
      return <Notifications workspace={workspace} />;
    // The one destination that takes an OPTIONAL record: the list is a screen
    // in its own right, and a room id opens that room on top of it.
    case "viewing-rooms":
      return (
        <ViewingRooms
          handle={principal.handle}
          record={record}
          roomsHref={workspacePath(workspace, "viewing-rooms")}
        />
      );
    // `record` is non-null here by construction: resolveWorkspaceRoute sends a
    // work destination without one back to the spec's recordFallback.
    case "work":
      return record ? (
        // NO `actingAs` HERE, AND THE OMISSION IS THE STATEMENT — OND-68. A
        // collector workspace is one person's; there is no firm whose trail
        // their reads could belong to, so the prop's default null is the right
        // answer and writing `actingAs={null}` would imply a seat that could
        // have been named. Their own reads are still recorded, and are theirs
        // alone to read.
        <WorkRecord
          workId={record}
          collectionHref={collectionHref}
          canAssignAgent={capabilities.assignAgent}
          canConsign={capabilities.consignWork}
          // THE OWNER'S OWN SALE — OND-173, and the capability is PASSED rather
          // than read here or inferred from the workspace. `transferWork` was
          // false for a collector until 20261014093000 built the act; it is
          // true now, and this prop is the door that keeps a true capability
          // from standing in front of somebody with nothing to press, which
          // OND-173 calls "the same failure as a feature flag, reached from the
          // other side". The control itself is on the work's own record, beside
          // the consignment panel; there is no rail destination for it, because
          // a sale is about one work and the rail is about sets of them.
          canSell={capabilities.transferWork}
        />
      ) : null;
    default:
      return <NotBuiltYet copy={view.notYet} />;
  }
}
